/*
 * ============================================================
 * SAV_ERP — Smart Account Vision Enterprise Resource Planning
 * Copyright (c) 2026 Hatem Mohamed Abdalla.
 * All rights reserved.
 *
 * This file is part of a proprietary software product owned
 * solely by Hatem Mohamed Abdalla (UAE, +971 50 390 9781).
 * Copying, redistribution, modification, reverse engineering
 * or reuse in any form without prior written consent of the
 * copyright owner is strictly prohibited and will be
 * prosecuted under UAE Federal Law No. 38/2021 on Copyright
 * and applicable international treaties.
 *
 * Fingerprint: SAV-ERP-HMA-2026-#a7f3d0
 * ============================================================
 */
import axios from 'axios';
import { getAuthToken, getAuthContext, markSessionExpired } from './authStorage';
import { buildBackendUrl, getBackendUrl } from '../utils/backendUrl';

let _lastPermToastAt = 0;
// Dependency-free permission-denied banner (the app ships no toast library).
const _notifyPermissionDenied = () => {
  const now = Date.now();
  if (now - _lastPermToastAt < 2500) return; // debounce bursts
  _lastPermToastAt = now;
  if (typeof document === 'undefined') return;
  try {
    const el = document.createElement('div');
    el.setAttribute('role', 'alert');
    el.setAttribute('data-testid', 'permission-denied-banner');
    el.textContent = 'ليست لديك صلاحية لهذا الإجراء / You do not have permission for this action';
    el.style.cssText = [
      'position:fixed', 'top:20px', 'left:50%', 'transform:translateX(-50%)',
      'z-index:99999', 'background:#b91c1c', 'color:#fff', 'padding:12px 20px',
      'border-radius:10px', 'font:600 14px system-ui,-apple-system,sans-serif',
      'box-shadow:0 8px 24px rgba(0,0,0,.25)', 'max-width:90vw', 'text-align:center',
    ].join(';');
    document.body.appendChild(el);
    setTimeout(() => { try { el.remove(); } catch (_e) { /* noop */ } }, 3200);
  } catch (_e) { /* DOM unavailable */ }
};

export const filenameFromDisposition = (disposition, fallbackFilename = 'download.bin') => {
  if (!disposition) return fallbackFilename;
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1] || fallbackFilename;
};

export const triggerBrowserDownload = (blob, filename = 'download.bin') => {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => { try { window.URL.revokeObjectURL(blobUrl); } catch (_) {} }, 30000);
};

export const BACKEND_URL = getBackendUrl();
export const API_BASE_URL = buildBackendUrl('/api');

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

// R378: financial POST requests keep the same idempotency key after a network/5xx
// failure. If the server committed but the response was lost, the next click replays
// the committed document instead of creating a duplicate invoice or voucher.
const FINANCIAL_CREATE_PATHS = new Set([
  '/sales-invoices',
  '/purchase-invoices',
  '/expense-invoices',
  '/receipts',
  '/payments',
]);
const IDEMPOTENCY_TTL_MS = 30 * 60 * 1000;

const normalizeRequestPath = (url = '') => {
  try {
    const parsed = new URL(String(url || ''), window.location.origin);
    return parsed.pathname.replace(/^\/api/, '') || '/';
  } catch (_) {
    return String(url || '').split('?')[0].replace(/^\/api/, '') || '/';
  }
};

const stableJsonValue = (value) => {
  if (value === null || value === undefined) return value ?? null;
  if (Array.isArray(value)) return value.map(stableJsonValue);
  if (Object.prototype.toString.call(value) === '[object Date]') return value.toISOString();
  if (typeof value === 'object' && !(typeof FormData !== 'undefined' && value instanceof FormData) && !(typeof Blob !== 'undefined' && value instanceof Blob)) {
    return Object.keys(value).sort().reduce((out, key) => {
      out[key] = stableJsonValue(value[key]);
      return out;
    }, {});
  }
  return value;
};

const fastHash = (text = '') => {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

const newRequestId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (_) {}
  return `sav-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const prepareFinancialIdempotency = (config) => {
  const method = String(config?.method || 'get').toLowerCase();
  const path = normalizeRequestPath(config?.url);
  if (method !== 'post' || !FINANCIAL_CREATE_PATHS.has(path) || typeof FormData !== 'undefined' && config?.data instanceof FormData) return;
  const bodyText = JSON.stringify(stableJsonValue(config?.data || {}));
  const fingerprint = `${method}:${path}:${fastHash(bodyText)}`;
  const storageKey = `sav:idempotency:${fingerprint}`;
  let record = null;
  try {
    record = JSON.parse(window.sessionStorage.getItem(storageKey) || 'null');
  } catch (_) {
    record = null;
  }
  const now = Date.now();
  if (!record?.key || !record?.createdAt || now - Number(record.createdAt) > IDEMPOTENCY_TTL_MS) {
    record = { key: newRequestId(), createdAt: now };
    try { window.sessionStorage.setItem(storageKey, JSON.stringify(record)); } catch (_) {}
  }
  config.headers = config.headers || {};
  config.headers['X-Idempotency-Key'] = record.key;
  config.headers['X-SAV-Request-ID'] = record.key;
  config.__savIdempotencyStorageKey = storageKey;
};

const clearFinancialIdempotency = (config) => {
  const storageKey = config?.__savIdempotencyStorageKey;
  if (!storageKey) return;
  try { window.sessionStorage.removeItem(storageKey); } catch (_) {}
};

const shouldInvalidateSession = (error) => {
  const status = error?.response?.status;
  if (status !== 401 && status !== 403) return false;
  const url = String(error?.config?.url || '').toLowerCase();
  const payload = error?.response?.data;
  const detail = String(payload?.detail || payload?.message || payload?.error || '').toLowerCase();
  if (url.includes('/auth/me') || url.includes('/auth/login') || url.includes('/auth/refresh')) return true;
  if (
    detail.includes('could not validate credentials') ||
    detail.includes('session expired') ||
    detail.includes('token expired') ||
    detail.includes('invalid token') ||
    detail.includes('credentials') ||
    detail.includes('jwt') ||
    detail.includes('account is deactivated') ||
    detail.includes('company is frozen') ||
    detail.includes('inactive')
  ) {
    return true;
  }
  return false;
};


export const applyDefaultAuthHeader = (token = '') => {
  if (token) {
    apiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common.Authorization;
  }
};

apiClient.interceptors.request.use((config) => {
  const nextConfig = config || {};
  const inferredContext = nextConfig.authContext || 'erp';
  nextConfig.authContext = inferredContext;
  nextConfig.headers = nextConfig.headers || {};
  if (nextConfig.skipAuthToken) {
    delete nextConfig.headers.Authorization;
    if (inferredContext === getAuthContext()) {
      applyDefaultAuthHeader('');
    }
    return nextConfig;
  }
  const token = getAuthToken(inferredContext);
  if (token) {
    nextConfig.headers.Authorization = `Bearer ${token}`;
    if (inferredContext === getAuthContext()) {
      applyDefaultAuthHeader(token);
    }
  } else {
    delete nextConfig.headers.Authorization;
    if (inferredContext === getAuthContext()) {
      applyDefaultAuthHeader('');
    }
  }
  prepareFinancialIdempotency(nextConfig);
  return nextConfig;
});

apiClient.interceptors.response.use(
  (response) => {
    clearFinancialIdempotency(response?.config);
    return response;
  },
  (error) => {
    const authContext = error?.config?.authContext || null;
    if (!error?.config?.skipAuthRedirect && shouldInvalidateSession(error)) {
      markSessionExpired(authContext);
    }
    const status = Number(error?.response?.status || 0);
    // Surface a clear permission-denied message instead of a silent console 403
    // (backend enforces authorization; this gives read-only roles visible feedback).
    if (status === 403 && !error?.config?.skipPermissionToast) {
      _notifyPermissionDenied();
    }
    // Validation/auth conflicts did not commit; a corrected submission needs a new key.
    // Network errors, timeouts and 5xx may have committed, so keep the key for replay.
    if (status >= 400 && status < 500 && status !== 409 && status !== 408) {
      clearFinancialIdempotency(error?.config);
    }
    return Promise.reject(error);
  }
);

export const apiGet = async (path, config = {}) => {
  const response = await apiClient.get(path, { authContext: 'erp', ...(config || {}) });
  return response.data;
};

export const apiPost = async (path, data, config = {}) => {
  const response = await apiClient.post(path, data, { authContext: 'erp', ...(config || {}) });
  return response.data;
};

export const apiPut = async (path, data, config = {}) => {
  const response = await apiClient.put(path, data, { authContext: 'erp', ...(config || {}) });
  return response.data;
};

export const apiPatch = async (path, data, config = {}) => {
  const response = await apiClient.patch(path, data, { authContext: 'erp', ...(config || {}) });
  return response.data;
};

export const apiDelete = async (path, config = {}) => {
  const response = await apiClient.delete(path, { authContext: 'erp', ...(config || {}) });
  return response.data;
};

export const apiDownload = async (path, config = {}) => {
  const mergedConfig = { timeout: 300000, ...(config || {}) };
  const response = await apiClient.get(path, { authContext: 'erp', ...mergedConfig, responseType: 'blob' });
  return response;
};

export const apiDownloadToFile = async (path, fallbackFilename = 'download.bin', config = {}) => {
  const response = await apiDownload(path, { timeout: 300000, ...(config || {}) });
  const filename = filenameFromDisposition(response.headers?.['content-disposition'], fallbackFilename);
  triggerBrowserDownload(response.data, filename);
  return { filename };
};

export { getBackendUrl, buildBackendUrl };
export default apiClient;

applyDefaultAuthHeader(getAuthToken(getAuthContext()));
